import { useDeleteApprovedVendorsMutation } from "@common/api/approved-vendors"
import { formatCount } from "@common/lib/number"
import { handleError } from "@common/utils"
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router"
import z from "zod"
import { DeleteDialog } from "@/components/layout/list-table-menu"
import useCustomToast from "@/hooks/use-custom-toast"
import { requirePermission } from "@/lib/require-permission"

const searchSchema = z.object({
  ids: z.preprocess(
    (value) =>
      Array.isArray(value) ? value : value === undefined ? undefined : [value],
    z.array(z.string().uuid()).catch([]),
  ),
})

export const Route = createFileRoute("/_layout/approved-vendors/delete/bulk")({
  validateSearch: (search) => searchSchema.parse(search),
  beforeLoad: async ({ context: { queryClient }, search }) => {
    await requirePermission(queryClient, "approved_vendor:delete", "/")
    if (search.ids.length === 0) {
      throw redirect({ to: "/approved-vendors", search: true })
    }
  },
  component: DeleteVendorsDialog,
})

function counted(count: number): string {
  return `${formatCount(count)} vendor${count === 1 ? "" : "s"}`
}

function DeleteVendorsDialog(): React.ReactElement {
  const { ids } = Route.useSearch()
  const navigate = useNavigate()
  const showToast = useCustomToast()
  const remove = useDeleteApprovedVendorsMutation()

  const close = () =>
    navigate({
      from: Route.fullPath,
      to: "/approved-vendors",
      search: ({ ids: _, ...previous }) => previous,
    })

  return (
    <DeleteDialog
      noun={ids.length === 1 ? "vendor" : counted(ids.length)}
      pending={remove.isPending}
      onCancel={close}
      onConfirm={() =>
        remove.mutate(
          { approvedVendorBulkIds: { vendor_ids: ids } },
          {
            onSuccess: ({ deleted }) => {
              showToast(
                "Deleted",
                `${counted(deleted.length)} deleted.`,
                "success",
              )
              close()
            },
            onError: (error) => handleError(error, showToast),
          },
        )
      }
    />
  )
}
